import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../config/supabaseClient";
import { FaChevronLeft, FaBookOpen } from "react-icons/fa";

// 🔹 Normalize for case-insensitive matching
const normalize = (str) =>
  str.toLowerCase().replace(/\s+/g, " ").trim();

// 🔹 Shared slugify helper
const slugify = (str) =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const Notes = () => {
  const params = useParams();
  const selectedClass = params.selectedClass;
  const subCategory = params.category || params.subcategory || params.subCategory;
  const navigate = useNavigate();

  // Hardcoded categories only
  const grammarOnly = [
    "Letters",
    "Stories",
    "Applications",
    "Translations",
    "Conditional Sentences",
    "Tenses",
    "MCQ Test",
    "Idioms",
    "Direct & Indirect",
  ];
  const commonHardcoded = ["Past Papers", "Model Papers", "Book Lessons", "MCQ Test"];

  const [subCategories, setSubCategories] = useState([]);
  const [topics, setTopics] = useState({});
  const [loading, setLoading] = useState(false);
  const [openSubCatId, setOpenSubCatId] = useState(null);
  const [subCatCounts, setSubCatCounts] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchActiveSubCategories = async () => {
      setLoading(true);
      try {
        let data, error;
        if (normalize(selectedClass) === "grammar") {
          // Fetch all topics to filter in JS for active grammar subcategories
          const result = await supabase
            .from('topics')
            .select('subCategory, class');
          data = result.data;
          error = result.error;
        } else {
          const result = await supabase
            .from('topics')
            .select('subCategory')
            .eq('class', `Class ${selectedClass}`);
          data = result.data;
          error = result.error;
        }

        if (error) throw error;

        const counts = {};
        (data || []).forEach(doc => {
          if (doc.subCategory) {
            counts[doc.subCategory] = (counts[doc.subCategory] || 0) + 1;
          }
          if (normalize(selectedClass) === "grammar" && doc.class && !doc.subCategory) {
            const normalizedClass = doc.class.trim();
            const matchedCat = grammarOnly.find(cat => normalize(cat) === normalize(normalizedClass));
            if (matchedCat) {
              counts[matchedCat] = (counts[matchedCat] || 0) + 1;
            }
          }
        });

        setSubCatCounts(counts);

        const baseList = normalize(selectedClass) === "grammar" ? grammarOnly : commonHardcoded;

        const filtered = baseList.filter(subCat => counts[subCat] > 0);

        setSubCategories(filtered);
      } catch (err) {
        console.error("Error fetching active subcategories:", err);
        setSubCategories(normalize(selectedClass) === "grammar" ? grammarOnly : commonHardcoded);
      } finally {
        setLoading(false);
      }
    };

    fetchActiveSubCategories();
  }, [selectedClass]);

  useEffect(() => {
    if (subCategory && subCategories.length > 0) {
      const index = subCategories.findIndex(
        (name) => normalize(name) === normalize(subCategory)
      );
      if (index !== -1) {
        setOpenSubCatId(index);
        subscribeToTopics(subCategory); // ✅ Live updates
      }
    }
  }, [subCategory, subCategories]);

  // 🔹 Subscribe to topics in realtime
  const subscribeToTopics = (subCatName) => {
    setLoading(true);

    let classFilter;
    if (normalize(selectedClass) === "grammar") {
      classFilter = "grammar";
    } else {
      classFilter = `Class ${selectedClass}`;
    }

    // Function to fetch and update state
    const fetchData = async () => {
      try {
        let query = supabase.from('topics').select('*');
        if (normalize(selectedClass) === "grammar") {
          query = query.or(`subCategory.eq."${subCatName}",class.eq."${subCatName}"`);
        } else {
          query = query.eq('class', classFilter).eq('subCategory', subCatName);
        }
        const { data, error } = await query;

        if (error) {
          console.error("Error fetching topics:", error);
          setLoading(false);
          return;
        }

        const topicData = {};
        (data || []).forEach((doc) => {
          // doc is the data object
          if (doc.topic) {
            const key = normalize(doc.topic);
            topicData[key] = {
              ...doc,
              slug: doc.slug || slugify(doc.topic),
            };
          }
        });

        // Sort topics
        const sortedKeys = Object.keys(topicData).sort((a, b) => {
          const numA = parseInt(a.match(/^\d+/)?.[0]);
          const numB = parseInt(b.match(/^\d+/)?.[0]);
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
          return a.localeCompare(b);
        });

        const sortedTopics = {};
        sortedKeys.forEach((key) => {
          sortedTopics[key] = topicData[key];
        });

        setTopics(sortedTopics);
      } catch (err) {
        console.error("Error processing topics:", err);
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchData();

    // Realtime Subscription
    const channel = supabase
      .channel(`public:topics:${classFilter}:${subCatName}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'topics',
          filter: `subCategory=eq.${subCatName}`, // Filter by subCategory
        },
        (payload) => {
          // On any change, re-fetch to ensure consistency and correct sorting
          // We could optimize to check payload.new.class === classFilter, but re-fetching is safer for now.
          fetchData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  // 🔹 Handle subcategory click
  const handleSubCategoryClick = (subCatName, index) => {
    const newOpenId = openSubCatId === index ? null : index;
    setOpenSubCatId(newOpenId);

    if (newOpenId !== null) {
      subscribeToTopics(subCatName);
      navigate(`/notes/${selectedClass}/${subCatName}`);
    } else {
      setTopics({});
      navigate(`/notes/${selectedClass}`);
    }

    setTimeout(() => {
      const cardElement = document.querySelectorAll(".subject-card")[index];
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  // 🔹 Handle topic click
  const handleTopicClick = (topicKey) => {
    const fileData = topics[topicKey];
    if (!fileData) return;

    const topicSlug = fileData.slug;
    const currentSubCategory = subCategories[openSubCatId];

    navigate(
      `/description/${encodeURIComponent(currentSubCategory)}/${encodeURIComponent(
        topicSlug
      )}`
    );
  };

  // 🔹 Styles
  const containerStyle = {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "20px 20px 250px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "24px",
    alignItems: "flex-start",
  };

  const cardStyle = (isActive) => ({
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
    border: `2px solid ${isActive ? "#007bff" : "transparent"}`,
    transition: "all 0.3s ease",
    overflow: "visible",
    position: "relative",
  });

  const headerStyle = {
    padding: "16px 20px",
    fontSize: "18px",
    fontWeight: "600",
    backgroundColor: "#f1f3f5",
    color: "#343a40",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: "12px 12px 0 0",
    transition: "background 0.2s",
  };

  const dropdownStyle = {
    backgroundColor: "#ffffff",
    borderTop: "1px solid #e9ecef",
    borderRadius: "0 0 12px 12px",
    maxHeight: "60vh",
    overflowY: "auto",
    position: "relative",
    zIndex: 10,
  };

  const dropdownContentStyle = {
    padding: "20px",
  };

  const topicsGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: "12px",
    marginTop: "10px",
  };

  const topicItemStyle = {
    background: "#ffffff",
    border: "1px solid #dee2e6",
    padding: "12px 15px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "15px",
    color: "#343a40",
    transition: "0.2s ease",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    wordWrap: "break-word",
  };

  const loadingStyle = {
    padding: "20px 0",
    color: "#6c757d",
    fontStyle: "italic",
    textAlign: "center",
  };

  return (
    <div style={containerStyle}>
      <main>
        {/* Centered Header Row with inline Back Button */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "20px", flexWrap: "wrap", marginBottom: "12px", marginTop: "10px" }}>
          <button 
            onClick={() => navigate(-1)} 
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              color: '#64748b',
              padding: '8px 18px',
              borderRadius: '30px',
              fontSize: '0.9rem',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f8fafc';
              e.currentTarget.style.color = '#1e293b';
              e.currentTarget.style.borderColor = '#cbd5e1';
              e.currentTarget.style.transform = 'translateX(-3px)';
              e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.color = '#64748b';
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.transform = 'translateX(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)';
            }}
          >
            <FaChevronLeft size={10} style={{ marginRight: '6px' }} /> Back
          </button>

          <h2 style={{ fontSize: "2.2rem", fontWeight: "800", color: "#1e293b", margin: 0, letterSpacing: "-0.5px" }}>
            Welcome to Our Educational Portal
          </h2>
        </div>
        <div style={{ height: "4px", width: "80px", background: "linear-gradient(135deg, #4f46e5, #3b82f6)", borderRadius: "10px", margin: "0 auto 20px" }}></div>
        <p style={{ textAlign: "center", fontSize: "1.1rem", color: "#64748b", fontWeight: "500", maxWidth: "600px", margin: "0 auto 40px" }}>
          Our goal is to provide high-quality educational resources.
        </p>

        <div style={gridStyle}>
          {subCategories.map((subCatName, index) => (
            <div
              key={index}
              className="subject-card"
              style={cardStyle(openSubCatId === index)}
            >
              <div
                style={headerStyle}
                onClick={() => handleSubCategoryClick(subCatName, index)}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#e9ecef";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#f1f3f5";
                }}
              >
                <span>{subCatName}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    background: '#e6f7ff',
                    color: '#1890ff',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    {subCatCounts[subCatName] || 0}
                  </span>
                  <span>{openSubCatId === index ? "▼" : "►"}</span>
                </div>
              </div>

              {openSubCatId === index && (
                <div style={dropdownStyle}>
                  <div style={dropdownContentStyle}>
                    {loading ? (
                      <div style={loadingStyle}>Loading...</div>
                    ) : Object.keys(topics).length > 0 ? (
                      <div style={topicsGridStyle}>
                        {Object.keys(topics).map((topicKey, i) => (
                          <div
                            key={i}
                            style={topicItemStyle}
                            onClick={() => handleTopicClick(topicKey)}
                            onMouseEnter={(e) => {
                              e.target.style.background = "#f1f9ff";
                              e.target.style.color = "#007bff";
                              e.target.style.borderColor = "#007bff";
                              e.target.style.transform = "translateY(-1px)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = "#ffffff";
                              e.target.style.color = "#343a40";
                              e.target.style.borderColor = "#dee2e6";
                              e.target.style.transform = "translateY(0)";
                            }}
                          >
                            <FaBookOpen size={13} style={{ marginRight: '8px', color: '#3b82f6' }} /> {topics[topicKey]?.topic || topicKey}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={loadingStyle}>No topics available</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Notes;